import schoolLogo from '@/assets/mfu_logo.png'
import {
  buildAgreementSignaturePreview,
  isCenteredAgreementSection,
  isSignatureAgreementSection,
  normalizeAgreementSections
} from '@/utils/agreement-preview-sections'
import { buildAgreementDocxBlob } from '@/utils/word-export'

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function safeFileBase(value) {
  return String(value || 'agreement')
    .trim()
    .replace(/[^a-z0-9-_]+/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase() || 'agreement'
}

function printableAssetUrl(value) {
  const url = String(value || '').trim()
  if (!url) return ''
  if (/^(data:|blob:|https?:\/\/)/i.test(url)) return url
  if (typeof window === 'undefined' || !window.location) return url
  try {
    return url.startsWith('/')
      ? new URL(url, window.location.origin).href
      : new URL(`/${url.replace(/^\.?\//, '')}`, window.location.origin).href
  } catch (error) {
    return url
  }
}

function partnerLogoUrl(agreement) {
  const logo = agreement && agreement.partnerLogo
  if (!logo) return ''
  if (typeof logo === 'string') return printableAssetUrl(logo)
  return printableAssetUrl(logo.url || '')
}

function rightSignatureHeader(agreement) {
  return [agreement && agreement.partner, agreement && agreement.country].filter(Boolean).join('\n')
}

function buildDefaultSignatureSection(agreement) {
  const rightHeader = [agreement && agreement.partner, agreement && agreement.country].filter(Boolean).join(', ') || 'Partner Institution'
  return {
    key: 'signature-block',
    title: 'Signature Block',
    content: [
      `Mae Fah Luang University || ${rightHeader}`,
      '(Name) ........................................ || (Name) ........................................',
      '(Position) ........................................ || (Position) ........................................',
      'Date: || Date:'
    ].join('\n')
  }
}

function ensurePdfSectionsHaveSignature(sections, agreement) {
  const normalized = normalizeAgreementSections(sections)
  if (!normalized.length) return normalized
  return normalized.some((section) => isSignatureAgreementSection(section))
    ? normalized
    : normalized.concat([buildDefaultSignatureSection(agreement)])
}

function lineBreaksToHtml(value) {
  return escapeHtml(value).replace(/\r?\n/g, '<br>')
}

function normalizeCoverTitleLines(value) {
  if (Array.isArray(value)) {
    return value
      .map((line) => String(line || '').trim())
      .filter(Boolean)
  }
  return String(value || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
}

function isAgreementTitleBlockSection(section) {
  const key = String(section && section.key ? section.key : '').trim().toLowerCase()
  return key === 'title-block'
}

function buildDefaultCoverTitleLines(agreement) {
  const activityText = `${agreement && (agreement.activityType || agreement.type) || ''} ${agreement && agreement.title || ''}`.toLowerCase()
  const isStudentExchange = activityText.includes('exchange') && activityText.includes('student')
  if (isStudentExchange) {
    const partnerLine = [agreement && agreement.partner, agreement && agreement.country].filter(Boolean).join(', ') || 'Partner Institution'
    return [
      'Agreement on Student Exchange',
      'between',
      'Mae Fah Luang University, Kingdom of Thailand',
      'and',
      partnerLine
    ]
  }
  return agreement && agreement.title ? [agreement.title] : []
}

function getCoverTitleLines(agreement, sections, options = {}) {
  if (options.includeCoverTitle === false) return []
  const overrideLines = normalizeCoverTitleLines(options.coverTitleLines)
  if (overrideLines.length) return overrideLines
  const titleBlock = (Array.isArray(sections) ? sections : []).find(isAgreementTitleBlockSection)
  const contentLines = normalizeCoverTitleLines(titleBlock && titleBlock.content)
  return contentLines.length ? contentLines : buildDefaultCoverTitleLines(agreement)
}

function renderCoverTitleHtml(lines) {
  const titleLines = Array.isArray(lines) ? lines.filter((line) => String(line || '').trim()) : []
  if (!titleLines.length) return '<p> </p>'
  return titleLines
    .map((line, index) => `<p class="${index === 0 ? 'cover-title__main' : ''}">${escapeHtml(line)}</p>`)
    .join('')
}

function renderSignatureTable(section, agreement) {
  const signature = buildAgreementSignaturePreview(section, {
    leftHeader: 'Mae Fah Luang University',
    rightHeader: rightSignatureHeader(agreement)
  })
  const displayHeader = (value) => String(value || '').trim() || '........................................'
  const displayName = (value) => String(value || '').trim() || '(Name) ........................................'
  const displayPosition = (value) => String(value || '').trim() || '(Position) ........................................'
  const displayDate = (value) => String(value || '').trim() || 'Date:'
  const cell = (value, extraClass = '') => `<div class="signature-cell ${extraClass}">${lineBreaksToHtml(value)}</div>`

  return `<div class="signature-preview">
    <div class="signature-row signature-row--header">
      ${cell(displayHeader(signature.left.header), 'signature-cell--header')}
      ${cell(displayHeader(signature.right.header), 'signature-cell--header')}
    </div>
    <div class="signature-row signature-row--sign">
      <div class="signature-cell"><div class="signature-line"></div></div>
      <div class="signature-cell"><div class="signature-line"></div></div>
    </div>
    <div class="signature-row">
      ${cell(displayName(signature.left.name), 'signature-cell--meta')}
      ${cell(displayName(signature.right.name), 'signature-cell--meta')}
    </div>
    <div class="signature-row">
      ${cell(displayPosition(signature.left.position), 'signature-cell--meta')}
      ${cell(displayPosition(signature.right.position), 'signature-cell--meta')}
    </div>
    <div class="signature-row">
      ${cell(displayDate(signature.left.date), 'signature-cell--meta')}
      ${cell(displayDate(signature.right.date), 'signature-cell--meta')}
    </div>
  </div>`
}

function renderSectionHtml(section, agreement) {
  if (isSignatureAgreementSection(section)) {
    return `<section>${renderSignatureTable(section, agreement)}</section>`
  }

  const centered = isCenteredAgreementSection(section, agreement && (agreement.activityType || agreement.type))
  const lines = String(section && section.content ? section.content : '')
    .split(/\r?\n/)
    .map(line => `<p>${escapeHtml(line || ' ')}</p>`)
    .join('')
  return `<section class="${centered ? 'section--centered' : ''}">${lines || '<p> </p>'}</section>`
}

export function downloadUrl(url, fileName) {
  if (!url || typeof document === 'undefined') return false
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.rel = 'noopener'
  link.download = fileName || ''
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  return true
}

export async function downloadAgreementDocx(agreement, sections, options = {}) {
  if (!agreement) return false
  const normalizedSections = normalizeAgreementSections(sections)
  if (normalizedSections.length && typeof URL !== 'undefined' && typeof document !== 'undefined') {
    const blob = await buildAgreementDocxBlob(agreement.title || 'Agreement', normalizedSections, {
      activityType: agreement.activityType || agreement.type || '',
      partner: agreement.partner || '',
      country: agreement.country || '',
      includeDocumentTitle: false,
      includeSectionTitles: false,
      includeMfuLogo: true,
      mfuLogo: schoolLogo,
      partnerLogo: agreement.partnerLogo || null,
      ...options
    })
    const objectUrl = URL.createObjectURL(blob)
    const didDownload = downloadUrl(objectUrl, `${safeFileBase(agreement.title)}.docx`)
    const timeout = typeof window !== 'undefined' && window.setTimeout ? window.setTimeout : setTimeout
    timeout(() => URL.revokeObjectURL(objectUrl), 1000)
    return didDownload
  }

  const finalDocument = agreement.finalDocument || {}
  if (finalDocument.url) {
    return downloadUrl(finalDocument.url, finalDocument.fileName || `${safeFileBase(agreement.title)}.docx`)
  }

  const uploadedDraft = agreement.uploadedDraft || {}
  if (uploadedDraft.url) {
    return downloadUrl(uploadedDraft.url, uploadedDraft.name || `${safeFileBase(agreement.title)}.docx`)
  }

  return false
}

export function downloadAgreementPdf(agreement, sections, options = {}) {
  if (!agreement || typeof window === 'undefined') return false
  const popup = window.open('', '_blank')
  if (!(popup && popup.document)) return false

  const normalizedSections = ensurePdfSectionsHaveSignature(sections, agreement)
  const coverTitleLines = getCoverTitleLines(agreement, normalizedSections, options)
  const bodySections = normalizedSections.filter((section) => !isAgreementTitleBlockSection(section))
  const mfuLogo = printableAssetUrl(schoolLogo)
  const logoRight = partnerLogoUrl(agreement)
  const sectionHtml = bodySections.length
    ? bodySections.map((section) => renderSectionHtml(section, agreement)).join('')
    : ''

  try {
    popup.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(agreement.title || 'Agreement')}</title>
  <style>
    body { color: #111827; font-family: "Times New Roman", Times, serif; margin: 40px; }
    .header { margin-bottom: 28px; text-align: center; }
    .logo-row { align-items: center; display: grid; gap: 24px; grid-template-columns: 150px 1fr 150px; margin-bottom: 18px; }
    .logo { align-items: center; border: 1px solid #d1d5db; display: flex; font-size: 10pt; height: 96px; justify-content: center; margin: 0 auto; padding: 8px; width: 96px; }
    .logo img { max-height: 100%; max-width: 100%; object-fit: contain; }
    .cover-title { margin: 0 auto; max-width: 560px; }
    .cover-title p { font-size: 13pt; font-weight: 700; line-height: 1.35; margin: 0 0 5px; white-space: pre-wrap; }
    .cover-title__main { font-size: 20pt !important; margin-bottom: 8px !important; }
    h2 { font-size: 15pt; margin: 22px 0 8px; }
    p { font-size: 12pt; line-height: 1.45; margin: 0 0 8px; white-space: pre-wrap; }
    .section--centered { text-align: center; }
    section:not(.section--centered) > p { text-align: justify; text-justify: inter-word; }
    .signature-preview { border: 1px solid #d7dde8; margin-top: 12px; page-break-inside: avoid; }
    .signature-row { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .signature-row + .signature-row { border-top: 1px solid #d7dde8; }
    .signature-cell { border-left: 1px solid #d7dde8; font-size: 12pt; line-height: 1.45; min-height: 24px; padding: 10px 12px; white-space: pre-wrap; }
    .signature-cell:first-child { border-left: 0; }
    .signature-cell--header { font-weight: 700; }
    .signature-row--sign .signature-cell { align-items: flex-end; display: flex; min-height: 120px; }
    .signature-line { border-bottom: 2px dotted #111827; width: 100%; }
    @media print { body { margin: 24mm; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo-row">
      <div>
      <div class="logo">${mfuLogo ? `<img src="${escapeHtml(mfuLogo)}" alt="MFU logo">` : ''}</div>
      </div>
      <div></div>
      <div>
      <div class="logo">${logoRight ? `<img src="${escapeHtml(logoRight)}" alt="Partner logo">` : escapeHtml(agreement.partner || 'Partner')}</div>
      </div>
    </div>
    <div class="cover-title">${renderCoverTitleHtml(coverTitleLines)}</div>
  </div>
  ${sectionHtml}
  <script>
    window.onload=function(){
      var images=Array.prototype.slice.call(document.images||[]);
      var waits=images.map(function(image){
        if(image.complete) return Promise.resolve();
        return new Promise(function(resolve){
          image.onload=resolve;
          image.onerror=resolve;
        });
      });
      Promise.all(waits).then(function(){
        window.focus();
        window.print();
      });
    };
  <\/script>
</body>
</html>`)
    popup.document.close()
    return true
  } catch (error) {
    if (typeof console !== 'undefined' && console.warn) console.warn('Unable to prepare agreement PDF.', error)
    if (popup && typeof popup.close === 'function') popup.close()
    return false
  }
}

export function downloadAgreementAs(agreement, sections, format, options = {}) {
  if (String(format || '').toLowerCase() === 'pdf') return downloadAgreementPdf(agreement, sections)
  return downloadAgreementDocx(agreement, sections, options)
}
